import bmwGroups from "../data/main-groups/bmw.json" with { type: "json" };
import vwGroupGroups from "../data/main-groups/vw_group.json" with { type: "json" };
import munich from "../data/make-packages/munich.json" with { type: "json" };
import zuffenhausen from "../data/make-packages/zuffenhausen.json" with { type: "json" };
import { DATA_LANGUAGES, type DataLanguage } from "./vocabularies.ts";

/**
 * The reference data in `data/`: make packages with their chassis code lists, and the main-group
 * lists they offer. The files hold data only; this module checks their shape once when loaded and
 * hands out typed lists. A new package or list needs its id here as well as its file.
 */

/** Make packages, named after the makers' home towns (ADR 0014). */
export const MAKE_PACKAGE_IDS = ["munich", "zuffenhausen"] as const;
export type MakePackageId = (typeof MAKE_PACKAGE_IDS)[number];

/** The parts catalogues whose main groups make packages offer. */
export const MAIN_GROUP_LIST_IDS = ["bmw", "vw_group"] as const;
export type MainGroupListId = (typeof MAIN_GROUP_LIST_IDS)[number];

export const CHASSIS_CODE_LIST_IDS = ["bmw", "bmw_m", "mini", "motorrad", "porsche"] as const;
export type ChassisCodeListId = (typeof CHASSIS_CODE_LIST_IDS)[number];

export interface MainGroup {
  /**
   * The group's number in the maker's parts catalogue, e.g. the first two digits of a BMW part
   * number; null for tags that span groups.
   */
  number: string | null;
  name: Record<DataLanguage, string>;
}

export interface ChassisCodeEntry {
  /**
   * The manufacturer's development code, e.g. "E90", or a body sub-code such as "E46/2". A
   * variant whose parts don't interchange with the base model carries its model in brackets:
   * after BMW's own code where one is known ("E36/2S (M3)"), else after the shared code
   * ("E92 (M3)", "G26 (i4)", "K50 (R1250GS)").
   */
  code: string;
  model: Record<DataLanguage, string>;
  /** Approximate production years; `to` is null while the model is still built. */
  from: number;
  to: number | null;
  /** A body sub-code's generation: parts marked with it fit this body too ("E46" for "E46/2"). */
  parent?: string;
}

/** A built-in list of chassis codes, as a make package brings it. */
export interface ChassisCodeList {
  entries: readonly ChassisCodeEntry[];
  /**
   * Whether a variant's bare code stands for its bracketed entry ("E36/2S" for "E36/2S (M3)").
   * Stored keys are normalized on every read, so an alias added later would change what a
   * garage's own code means: only the lists that had aliases before others shipped keep them.
   */
  aliases: boolean;
}

export interface MakePackage {
  /** The makes it covers, to describe it; never a package's name. */
  makes: readonly string[];
  /** The main groups it offers as tags; makers that share a parts catalogue share a list. */
  mainGroups: MainGroupListId;
  /** The chassis code lists it offers to pre-fill from, in the order they are shown. */
  chassisCodeLists: readonly ChassisCodeListId[];
  /** Where its lists were compiled from. */
  sources: string;
}

export interface ReferenceData {
  makePackages: Record<MakePackageId, MakePackage>;
  mainGroupLists: Record<MainGroupListId, readonly MainGroup[]>;
  chassisCodeLists: Record<ChassisCodeListId, ChassisCodeList>;
}

/** One file as read: its name for messages, and its parsed content. */
export interface ReferenceFile {
  name: string;
  content: unknown;
}

/** The data files failed their check; `problems` names each wrong field. */
export class ReferenceDataError extends Error {
  readonly problems: readonly string[];
  constructor(problems: readonly string[]) {
    super(`Reference data is invalid:\n${problems.map((p) => `- ${p}`).join("\n")}`);
    this.problems = problems;
  }
}

/** The form two spellings of a code share before aliases: no case, spaces, hyphens or brackets. */
export function squashChassisCode(code: string): string {
  return code.replace(/[\s()-]+/g, "").toUpperCase();
}

type Obj = Record<string, unknown>;

function isObj(value: unknown): value is Obj {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === "string" && value.trim() === value && value.length > 0;
}

function isYear(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 1900 && (value as number) <= 2100;
}

function oneOf<T extends string>(ids: readonly T[], value: unknown): value is T {
  return typeof value === "string" && (ids as readonly string[]).includes(value);
}

/**
 * Checks the files and builds the typed data. Every problem is collected before failing, each with
 * the file and the path of the wrong field, so a broken file is fixed in one go.
 */
export function readReferenceData(
  packageFiles: readonly ReferenceFile[],
  mainGroupFiles: readonly ReferenceFile[],
): ReferenceData {
  const problems: string[] = [];
  const makePackages: Partial<Record<MakePackageId, MakePackage>> = {};
  const mainGroupLists: Partial<Record<MainGroupListId, readonly MainGroup[]>> = {};
  const chassisCodeLists: Partial<Record<ChassisCodeListId, ChassisCodeList>> = {};
  const seenCodes = new Map<string, string>();

  function names(at: string, value: unknown): Record<DataLanguage, string> | null {
    if (!isObj(value)) {
      problems.push(`${at}: expected an object with ${DATA_LANGUAGES.join(", ")}`);
      return null;
    }
    let ok = true;
    for (const language of DATA_LANGUAGES) {
      if (!isText(value[language])) {
        problems.push(`${at}.${language}: missing or not a trimmed text`);
        ok = false;
      }
    }
    return ok ? (value as Record<DataLanguage, string>) : null;
  }

  function entries(at: string, value: unknown): ChassisCodeEntry[] {
    if (!Array.isArray(value) || value.length === 0) {
      problems.push(`${at}: expected a non-empty list`);
      return [];
    }
    const codes = new Set(value.map((e) => (isObj(e) ? e.code : undefined)));
    const result: ChassisCodeEntry[] = [];
    value.forEach((raw, i) => {
      const here = `${at}[${i}]`;
      if (!isObj(raw)) {
        problems.push(`${here}: expected an object`);
        return;
      }
      const { code, model, from, to, parent } = raw;
      if (!isText(code)) problems.push(`${here}.code: missing or not a trimmed text`);
      const label = names(`${here}.model`, model);
      if (!isYear(from)) problems.push(`${here}.from: expected a year`);
      if (to !== null && !isYear(to)) problems.push(`${here}.to: expected a year or null`);
      if (isYear(from) && isYear(to) && to < from) problems.push(`${here}.to: before from`);
      if (parent !== undefined && (!isText(parent) || !codes.has(parent) || parent === code)) {
        problems.push(`${here}.parent: not another code of the same list`);
      }
      if (!isText(code) || !label || !isYear(from) || (to !== null && !isYear(to))) return;
      const key = squashChassisCode(code);
      const other = seenCodes.get(key);
      if (other) problems.push(`${here}.code: "${code}" is already in ${other}`);
      seenCodes.set(key, here);
      result.push({
        code,
        model: label,
        from,
        to: to as number | null,
        ...(parent ? { parent: parent as string } : {}),
      });
    });
    return result;
  }

  for (const file of mainGroupFiles) {
    const at = file.name;
    const data = file.content;
    if (!isObj(data) || !oneOf(MAIN_GROUP_LIST_IDS, data.id)) {
      problems.push(`${at}.id: expected one of ${MAIN_GROUP_LIST_IDS.join(", ")}`);
      continue;
    }
    if (mainGroupLists[data.id]) problems.push(`${at}.id: "${data.id}" is in two files`);
    if (!isText(data.sources)) problems.push(`${at}.sources: missing`);
    if (!Array.isArray(data.groups) || data.groups.length === 0) {
      problems.push(`${at}.groups: expected a non-empty list`);
      continue;
    }
    const groups: MainGroup[] = [];
    const numbers = new Set<string>();
    data.groups.forEach((raw: unknown, i: number) => {
      const here = `${at}.groups[${i}]`;
      if (!isObj(raw) || !isText(raw.number)) {
        problems.push(`${here}.number: missing or not a trimmed text`);
        return;
      }
      if (numbers.has(raw.number)) problems.push(`${here}.number: "${raw.number}" twice`);
      numbers.add(raw.number);
      const label = names(`${here}.name`, raw.name);
      if (label) groups.push({ number: raw.number, name: label });
    });
    mainGroupLists[data.id] = groups;
  }

  for (const file of packageFiles) {
    const at = file.name;
    const data = file.content;
    if (!isObj(data) || !oneOf(MAKE_PACKAGE_IDS, data.id)) {
      problems.push(`${at}.id: expected one of ${MAKE_PACKAGE_IDS.join(", ")}`);
      continue;
    }
    if (makePackages[data.id]) problems.push(`${at}.id: "${data.id}" is in two files`);
    const makes = data.makes;
    if (!Array.isArray(makes) || makes.length === 0 || !makes.every(isText)) {
      problems.push(`${at}.makes: expected a non-empty list of texts`);
    }
    if (!oneOf(MAIN_GROUP_LIST_IDS, data.mainGroups)) {
      problems.push(`${at}.mainGroups: expected one of ${MAIN_GROUP_LIST_IDS.join(", ")}`);
    }
    if (!isText(data.sources)) problems.push(`${at}.sources: missing`);
    if (!Array.isArray(data.chassisCodeLists)) {
      problems.push(`${at}.chassisCodeLists: expected a list`);
      continue;
    }
    const listIds: ChassisCodeListId[] = [];
    data.chassisCodeLists.forEach((raw: unknown, i: number) => {
      const here = `${at}.chassisCodeLists[${i}]`;
      if (!isObj(raw) || !oneOf(CHASSIS_CODE_LIST_IDS, raw.id)) {
        problems.push(`${here}.id: expected one of ${CHASSIS_CODE_LIST_IDS.join(", ")}`);
        return;
      }
      if (chassisCodeLists[raw.id]) problems.push(`${here}.id: "${raw.id}" is in two packages`);
      if (typeof raw.aliases !== "boolean")
        problems.push(`${here}.aliases: expected true or false`);
      chassisCodeLists[raw.id] = {
        entries: entries(`${here}.entries`, raw.entries),
        aliases: raw.aliases === true,
      };
      listIds.push(raw.id);
    });
    makePackages[data.id] = {
      makes: Array.isArray(makes) ? makes.filter(isText) : [],
      mainGroups: data.mainGroups as MainGroupListId,
      chassisCodeLists: listIds,
      sources: isText(data.sources) ? data.sources : "",
    };
  }

  for (const id of MAKE_PACKAGE_IDS)
    if (!makePackages[id]) problems.push(`package "${id}": no file`);
  for (const id of MAIN_GROUP_LIST_IDS) {
    if (!mainGroupLists[id]) problems.push(`main-group list "${id}": no file`);
  }
  for (const id of CHASSIS_CODE_LIST_IDS) {
    if (!chassisCodeLists[id]) problems.push(`chassis code list "${id}": in no package`);
  }
  if (problems.length > 0) throw new ReferenceDataError(problems);
  return {
    makePackages: makePackages as Record<MakePackageId, MakePackage>,
    mainGroupLists: mainGroupLists as Record<MainGroupListId, readonly MainGroup[]>,
    chassisCodeLists: chassisCodeLists as Record<ChassisCodeListId, ChassisCodeList>,
  };
}

/** The files shipped in `data/`, for checks that look at them as written. */
export const REFERENCE_FILES = {
  packages: [
    { name: "make-packages/munich.json", content: munich },
    { name: "make-packages/zuffenhausen.json", content: zuffenhausen },
  ],
  mainGroups: [
    { name: "main-groups/bmw.json", content: bmwGroups },
    { name: "main-groups/vw_group.json", content: vwGroupGroups },
  ],
} as const satisfies Record<string, readonly ReferenceFile[]>;

const DATA = readReferenceData(REFERENCE_FILES.packages, REFERENCE_FILES.mainGroups);

export const MAKE_PACKAGES: Record<MakePackageId, MakePackage> = DATA.makePackages;

export const MAIN_GROUP_LISTS: Record<MainGroupListId, readonly MainGroup[]> = DATA.mainGroupLists;

/** The built-in lists by id. */
export const BUILT_IN_CHASSIS_CODE_LISTS: Record<ChassisCodeListId, ChassisCodeList> =
  DATA.chassisCodeLists;

/** Built-in chassis codes the user can pre-fill from, per list. */
export const CHASSIS_CODE_LISTS = Object.fromEntries(
  CHASSIS_CODE_LIST_IDS.map((list) => [list, BUILT_IN_CHASSIS_CODE_LISTS[list].entries]),
) as Record<ChassisCodeListId, readonly ChassisCodeEntry[]>;
